/**
 * BookingCancelledPage
 *
 * Shown to patients after they click the cancel link in their
 * confirmation or reminder email.
 *
 * Handles:
 * - Success: Shows cancellation confirmation + rebooking notice
 * - Already cancelled: Shows already-cancelled message
 * - Error: Shows error message with contact info
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { XCircle, AlertCircle, Phone, Mail, RefreshCw } from 'lucide-react';

function BookingCancelledPage() {
  const [searchParams] = useSearchParams();

  const name = searchParams.get('name');
  const rebooking = searchParams.get('rebooking');
  const already = searchParams.get('already');
  const reason = searchParams.get('reason');

  // Error state
  if (reason) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 40,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: '#fee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <AlertCircle size={32} color="#d9534f" />
          </div>

          <h1 style={{ color: '#d9534f', marginBottom: 15 }}>
            Cancellation Issue
          </h1>

          <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
            {reason === 'past'
              ? "This appointment has already passed and cannot be cancelled."
              : reason === 'error'
              ? "There was a problem processing your cancellation. Please contact our reception team."
              : "There was an issue with your cancellation request. Please contact our reception team."}
          </p>

          <div style={{
            backgroundColor: '#f5f5f5',
            padding: 20,
            borderRadius: 8,
            marginTop: 20,
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Contact Reception:</p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              <Phone size={18} />
              <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
            </p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Mail size={18} />
              <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already cancelled state
  if (already) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 40,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: '#fff3cd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <XCircle size={32} color="#856404" />
          </div>

          <h1 style={{ color: '#856404', marginBottom: 15 }}>
            Already Cancelled
          </h1>

          <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
            This appointment has already been cancelled. Check your email for new booking options.
          </p>

          <div style={{
            backgroundColor: '#f5f5f5',
            padding: 20,
            borderRadius: 8,
            marginTop: 15,
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Need help?</p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              <Phone size={18} />
              <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
            </p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Mail size={18} />
              <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 40,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#fdf2f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <XCircle size={48} color="#d9534f" />
        </div>

        <h1 style={{ color: '#d9534f', marginBottom: 15 }}>
          Appointment Cancelled
        </h1>

        <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
          {name ? `${name}, your` : 'Your'} appointment has been cancelled.
        </p>

        {rebooking === 'true' && (
          <div style={{
            backgroundColor: '#e8f5e9',
            borderLeft: '4px solid #5cb85c',
            padding: 20,
            borderRadius: '0 8px 8px 0',
            textAlign: 'left',
            marginBottom: 25,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <RefreshCw size={20} color="#5cb85c" />
              <span style={{ fontWeight: 'bold', fontSize: 16, color: '#333' }}>New Booking Options Sent</span>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              We've sent new appointment options to your email. Please check your inbox and select a new time that works for you.
            </p>
          </div>
        )}

        <div style={{
          backgroundColor: '#f5f5f5',
          padding: 20,
          borderRadius: 8,
          marginTop: 25,
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Need help?</p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <Phone size={18} />
            <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
          </p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Mail size={18} />
            <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
          </p>
        </div>

        <p style={{ marginTop: 25, fontSize: 14, color: '#999' }}>
          Anglesea Sports Medicine | Cambridge, New Zealand
        </p>
      </div>
    </div>
  );
}

export default BookingCancelledPage;
